// RigSentinel — Phase 1 Auth/Storage-Scoping.
//
// scopeKey(base) namespaces a storage key by the SIGNED-IN user's immutable
// server-issued uid: "rsc.wells.v1" -> "rsc.wells.v1::u_<uid>".
//
// STRICT RULE (user-mandated, do not weaken): if there is no uid, scopeKey
// returns null. It NEVER falls back to the unscoped base key. Every read/
// write helper below fails safe (no-op) rather than silently touching
// unscoped data — the only code allowed to read unscoped legacy keys is the
// explicit migration flow in lib/legacyClaim.js.

import { getCurrentUid } from "./authState";

function _warnBlocked(op, base) {
  try {
    // eslint-disable-next-line no-console
    console.warn(`[accountScope] Blocked ${op} on "${base}" — no authenticated user.`);
  } catch { /* noop */ }
}

export function scopeKey(base) {
  const uid = getCurrentUid();
  if (!uid) return null;
  return `${base}::u_${uid}`;
}

/** Suffix used for the currently signed-in user, for callers that need to
 *  filter a raw localStorage key list (e.g. Settings' "Export All Reports"). */
export function currentScopeSuffix() {
  const uid = getCurrentUid();
  return uid ? `::u_${uid}` : null;
}

export const scoped = {
  getItem(base) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("read", base); return null; }
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem(base, value) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("write", base); return; }
    try { localStorage.setItem(key, value); } catch { /* quota / unavailable */ }
  },
  removeItem(base) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("remove", base); return; }
    try { localStorage.removeItem(key); } catch { /* noop */ }
  },
};

export const scopedSession = {
  getItem(base) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("read", base); return null; }
    try { return sessionStorage.getItem(key); } catch { return null; }
  },
  setItem(base, value) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("write", base); return; }
    try { sessionStorage.setItem(key, value); } catch { /* quota / unavailable */ }
  },
  removeItem(base) {
    const key = scopeKey(base);
    if (!key) { _warnBlocked("remove", base); return; }
    try { sessionStorage.removeItem(key); } catch { /* noop */ }
  },
};
